import Heading from 'LuxKit/Typography/Heading'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import CategoryTile from 'LuxKit/CategoryTile'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import type { PlaceImage } from './ExperiencesPageHardcodedLists'
import { useLocalizedPlaces } from 'hooks/usePlace'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'

interface Props {
  locations: Array<PlaceImage>
}

const TILE_LOADING_STYLE = { width: rem(180), paddingTop: rem(8) }

function ExperiencesTrendingDestinationsCarousel({ locations }: Props) {
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const placeIds = useMemo(() => locations.map(location => location.place.id), [locations])
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const handleOnTileClick = useCallback(() => {
    trackEvent('TRENDING_TILE_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  return <>
    <Heading variant="heading3">
      {intl.formatMessage({
        id: 'xg_2zhy5zNbfN9w044e8',
        defaultMessage: 'Immerse yourself in these trending destinations',
        description: 'Heading for the trending destinations carousel on experiences landing page',
      })}
    </Heading>
    <CardCarousel pageSize={6} width={rem(180)}>
      {locations.map(location => {
        const localized = localizedPlaces[location.place.id]
        const destinationName = localized?.place?.name
        const isLoading = localized?.isLoading ?? true

        if (isLoading) {
          return <div key={location.place.id} style={TILE_LOADING_STYLE}>
            <TextLoadingBox width={120} typography="body-medium" />
          </div>
        }

        if (!destinationName) {
          return null
        }

        return <CategoryTile
          to={`/search/experiences?${replaceExperienceQueryString({
            destinationId: location.place.id,
            destinationName,
          })}`}
          key={location.place.id}
          image={{ id: location.imageId }}
          heading={destinationName}
          onClick={handleOnTileClick}
          align="bottom"
        />
      })}
    </CardCarousel>
  </>
}

export default ExperiencesTrendingDestinationsCarousel
